import { isSecondaryPersistedTabContent } from 'apiConnector/tabs/secondaryMarket.tabs.model';
import { difference, union } from 'lodash-es';
import type { Action } from 'state/actions';
import type { SecondaryOrderStatePath } from 'state/secondaryMarket/secondaryMarket.model';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import type { ChangeTrackForm, FormMap } from 'state/secondaryMarket/shared/form/input/input.model';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import { addKey, addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';

export function inputFormReducerFactory<
  F extends Form,
  C extends ChangeTrackForm<F>,
  ActionType extends Exclude<OrderActionType, 'CP_ORDER'>,
>(
  actionType: ActionType,
  initialState: FormMap<F, C>,
  initialForm: C,
  tabType: Exclude<SecondaryOrderStatePath, 'cpOrder'>,
) {
  const isSameTabType = (action: { tabType?: string }) => action.tabType === tabType;

  return (state: FormMap<F, C> = initialState, action: Action): FormMap<F, C> => {
    switch (action.type) {
      case 'SECONDARY_MARKET_QUOTES_WORKSPACE_ADD_TAB' as const:
        if (isSameTabType(action)) {
          return addKey(state, action.tabId, initialForm);
        }
        return state;
      case 'SECONDARY_MARKET_POSTEVENT_WORKSPACE_ADD_TAB' as const:
        return addKey(state, action.tabId, initialForm);
      case `SECONDARY_MARKET_AMEND_${actionType}_FETCHED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              tradeSizeInputMode: (action.data.tradeSizeInputMode as TradeSizeInputMode) ?? 'quantity',
            }) as Partial<C>,
        );
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FAILED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              salesId: undefined,
            }) as Partial<C>,
        );
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_RECEIVED` as const:
        return patchKey(state, action.tabId, s => {
          if (s.salesDesk !== action.response.selectedSale?.desk) {
            return {
              salesDesk: undefined,
            } as Partial<C>;
          }
          return s;
        });
      case `SECONDARY_MARKET_${actionType}_FORM_RESET` as const:
        return addOrReplaceKey(state, action.tabId, initialForm);
      case `SECONDARY_MARKET_${actionType}_FORM_FIELD_CHANGED` as const:
        if (action.trackChanges === true) {
          return patchKey<C, FormMap<F, C>, string>(
            state,
            action.tabId,
            s =>
              ({
                [action.field]: action.value,
                changes: union(s.changes, [action.field as keyof C]),
              }) as Partial<C>,
          );
        } else {
          return patchKey(
            state,
            action.tabId,
            s =>
              ({
                [action.field]: action.value,
                changes: difference(s.changes, [action.field as keyof F]),
              }) as Partial<C>,
          );
        }
      case `SECONDARY_MARKET_${actionType}_SETTLEMENT_DATE_RECEIVED` as const:
        return patchKey(
          state,
          action.tabId,
          () =>
            ({
              settlementDate: action.settlementDate,
            }) as Partial<C>,
        );
      case 'SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB' as const:
      case 'SECONDARY_MARKET_POSTEVENT_WORKSPACE_REMOVE_TAB' as const:
        return removeKey(state, action.tabId);
      case 'WORKSPACE_TAB_CONTENT_FETCHED' as const:
        if (isSameTabType(action) && isSecondaryPersistedTabContent<F>(action.tabContent)) {
          const {
            workspace: _workspace,
            bcsProductKey: _bcsProductKey,
            tradeSizeInputMode,
            ...formData
          } = action.tabContent;
          return addOrReplaceKey(state, action.tabId, {
            tradeSizeInputMode: tradeSizeInputMode ?? 'quantity',
            changes: [],
            ...formData,
          } as unknown as C);
        }
        return state;
      default:
        return state;
    }
  };
}
